import { useEffect, useState } from 'react';
import { api, type SotpState } from '../lib/api';
import { money } from '../lib/format';
import { Badge, Card, EmptyState, ErrorNotice, SectionHeading, Spinner } from './ui';
import { Icon } from './icons';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const KINDS = [
  ['surplus_asset', 'Surplus asset — added'],
  ['debt_like', 'Debt or debt-like — deducted'],
  ['central_provision', 'Group provision — deducted'],
  ['other', 'Other — enter the sign yourself'],
] as const;

const pct = (value: string): string => {
  const n = Number(value);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : value;
};

/**
 * Sum of the parts, and the corrections between the sum and the group.
 *
 * The screen is arranged around the three things that make a sum-of-the-parts
 * valuation wrong, because all three are omissions and none of them announces
 * itself.
 *
 * **The parts are read, not typed.** Each unit contributes the figure its own
 * calculation produced, and the row says which run. A group assembled from
 * re-keyed numbers is a second set of figures that can drift from the first,
 * and there is only ever meant to be one.
 *
 * **A unit nobody has valued stops the bridge.** It is listed at the top in
 * amber rather than quietly missing from the sum, because a group short by a
 * division looks exactly like a group.
 *
 * **Head office is asked for and cannot be skipped.** Divisions are usually
 * valued on earnings struck before it, so adding them together produces a
 * group with no head office in it. The form offers the two honest answers —
 * capitalise it, or state that the unit values already carry it — and has no
 * third option, because the third option is silence and silence is wrong more
 * often than either.
 */
export function SumOfParts({
  engagementId,
  canWrite,
  currency,
}: {
  engagementId: string;
  canWrite: boolean;
  currency: string;
}) {
  const [state, setState] = useState<SotpState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    try {
      setState(await api.sotp(engagementId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the parts');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  if (error && state === null) return <ErrorNotice title="Sum of the parts" message={error} />;
  if (state === null) return <Spinner label="Loading the parts" />;

  const parts = state.units.reduce((sum, u) => sum + Number(u.value), 0);

  return (
    <Card>
      <SectionHeading
        title="Sum of the parts"
        subtitle="The units added back into a group, and the three things that sit between the two"
        right={
          state.units.length > 0 ? (
            <Badge tone="slate">{state.units.length} valued</Badge>
          ) : null
        }
      />

      {state.unvalued.length > 0 ? (
        <p className="flex gap-2 px-6 pb-3 text-sm leading-relaxed text-amber-700">
          <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {state.unvalued.join(', ')} {state.unvalued.length === 1 ? 'has' : 'have'} no
            calculation behind {state.unvalued.length === 1 ? 'it' : 'them'}. A bridge is refused
            until every unit is valued or removed — a sum that leaves one out produces a group that
            looks complete and is short by a division.
          </span>
        </p>
      ) : null}

      {state.units.length === 0 ? (
        <EmptyState
          title="No valued units"
          detail="Each part contributes the figure its own valuation produced. Value the units first and they appear here; nothing on this screen is typed in twice."
        />
      ) : (
        <div className="overflow-x-auto px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3 font-medium">Unit</th>
                <th className="py-2 pr-3 font-medium">Valued on</th>
                <th className="py-2 text-right font-medium">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {state.units.map((u) => (
                <tr key={u.unitId}>
                  <td className="py-2 pr-3 text-slate-800">{u.name}</td>
                  <td className="py-2 pr-3 text-slate-500">
                    {u.method.replace(/_/g, ' ')}
                    {/* Which run, so the figure can be traced without leaving
                        the screen it is quoted on. */}
                    <span className="ml-2 text-xs text-slate-400">
                      run {u.runId.slice(0, 8)}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums text-slate-800">{money(u.value)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200">
                <td className="py-2 pr-3 font-medium text-slate-800">Sum of the parts</td>
                <td className="py-2 pr-3 text-xs text-slate-400">
                  not the value of the group
                </td>
                <td className="py-2 text-right font-medium tabular-nums text-slate-900">
                  {money(String(parts))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {error ? <p className="mt-3 px-6 text-sm text-rose-600">{error}</p> : null}

      {canWrite && state.units.length > 0 && state.unvalued.length === 0 ? (
        <div className="border-t border-slate-100 px-6 py-4">
          <button type="button" className="btn-primary" onClick={() => setOpen((v) => !v)}>
            {state.latestBridge ? 'Bridge it again' : 'Bridge it to a group value'}
          </button>
        </div>
      ) : null}

      {open ? (
        <BridgeForm
          engagementId={engagementId}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await load();
          }}
        />
      ) : null}

      {state.latestBridge ? (
        <BridgeResult outputs={state.latestBridge.outputs} currency={currency} />
      ) : null}
    </Card>
  );
}

function BridgeResult({
  outputs,
  currency,
}: {
  outputs: Record<string, any>;
  currency: string;
}) {
  const rows: [string, string, string?][] = [
    ['Sum of the parts', outputs.sum_of_parts],
  ];
  if (outputs.central_costs?.treatment === 'capitalised') {
    rows.push([
      `Central costs capitalised — ${money(outputs.central_costs.annual)} x ${Number(outputs.central_costs.multiple).toFixed(2)}`,
      `(${money(outputs.central_costs.deduction)})`,
    ]);
  } else {
    rows.push([
      'Central costs',
      '—',
      'stated to be inside the unit values already',
    ]);
  }
  for (const item of (outputs.group_items ?? []) as {
    label: string;
    kind: string;
    effect: string;
  }[]) {
    const effect = Number(item.effect);
    rows.push([
      `${item.label} — ${item.kind.replace(/_/g, ' ')}`,
      // Deductions in brackets, as they read on a schedule rather than as a
      // negative number in a column of positives.
      effect < 0 ? `(${money(String(Math.abs(effect)))})` : money(item.effect),
    ]);
  }
  if (Number(outputs.group_adjustment?.rate ?? 0) !== 0) {
    rows.push([
      `Group-level adjustment ${pct(outputs.group_adjustment.rate)}`,
      '',
      outputs.group_adjustment.basis,
    ]);
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <p className="text-sm font-medium text-slate-800">From the parts to the group</p>
      <table className="mt-2 w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.map(([label, value, note], i) => (
            <tr key={i}>
              <td className="py-2 pr-3 text-slate-700">
                {label}
                {note ? <span className="block text-xs text-slate-400">{note}</span> : null}
              </td>
              <td className="py-2 text-right tabular-nums text-slate-700">{value}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-200">
            <td className="py-2 pr-3 font-medium text-slate-800">Group value</td>
            <td className="py-2 text-right font-medium tabular-nums text-slate-900">
              {money(outputs.group_value)}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-400">{currency}</p>
    </div>
  );
}

function BridgeForm({
  engagementId,
  onClose,
  onSaved,
}: {
  engagementId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [inside, setInside] = useState(false);
  const [annual, setAnnual] = useState('');
  const [multiple, setMultiple] = useState('');
  const [basis, setBasis] = useState('');
  const [items, setItems] = useState<
    { label: string; kind: (typeof KINDS)[number][0]; value: string }[]
  >([]);
  const [rate, setRate] = useState('');
  const [adjustmentBasis, setAdjustmentBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = inside || (annual.trim() !== '' && multiple.trim() !== '');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.sotpBridge(engagementId, {
        centralCosts: inside
          ? { alreadyInUnitValues: true }
          : { annual: annual.trim(), multiple: multiple.trim(), basis: basis.trim() || undefined },
        groupItems: items
          .filter((i) => i.label.trim() !== '' && i.value.trim() !== '')
          .map((i) => ({ label: i.label.trim(), kind: i.kind, value: i.value.trim() })),
        ...(rate.trim() !== ''
          ? { groupAdjustment: { rate: rate.trim(), basis: adjustmentBasis.trim() } }
          : {}),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The bridge was refused');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-5">
      <p className="text-sm font-medium text-slate-800">Head office</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Divisions are usually valued on earnings struck before head office. Add them together and
        the answer is a group with no head office in it. There is no third option here, because the
        third option is silence and silence is wrong more often than either of these.
      </p>

      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={inside} onChange={(e) => setInside(e.target.checked)} />
        The unit values are already struck after their share of central costs
      </label>

      {!inside ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="text-sm text-slate-600">
            Annual central cost
            <input
              className={INPUT}
              inputMode="decimal"
              value={annual}
              placeholder="350000"
              onChange={(e) => setAnnual(e.target.value)}
            />
          </label>
          <label className="text-sm text-slate-600">
            Capitalised at
            <input
              className={INPUT}
              inputMode="decimal"
              value={multiple}
              placeholder="6.0"
              onChange={(e) => setMultiple(e.target.value)}
            />
          </label>
          <label className="text-sm text-slate-600">
            What it covers
            <input
              className={INPUT}
              value={basis}
              placeholder="Board and group finance"
              onChange={(e) => setBasis(e.target.value)}
            />
          </label>
        </div>
      ) : null}

      <p className="mt-5 text-sm font-medium text-slate-800">Group items</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Anything held at group level rather than inside a unit. The sign follows what it is, so a
        facility entered as debt is deducted whichever way round it is typed — and something
        already inside a unit's value belongs in neither list twice.
      </p>
      {items.map((item, i) => (
        <div key={i} className="mt-2 grid gap-3 sm:grid-cols-3">
          <input
            className={INPUT}
            value={item.label}
            placeholder="Group bank facility"
            onChange={(e) =>
              setItems((all) => all.map((x, j) => (i === j ? { ...x, label: e.target.value } : x)))
            }
          />
          <select
            className={SELECT}
            value={item.kind}
            onChange={(e) =>
              setItems((all) =>
                all.map((x, j) =>
                  i === j ? { ...x, kind: e.target.value as (typeof KINDS)[number][0] } : x,
                ),
              )
            }
          >
            {KINDS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            className={INPUT}
            inputMode="decimal"
            value={item.value}
            placeholder="1200000"
            onChange={(e) =>
              setItems((all) => all.map((x, j) => (i === j ? { ...x, value: e.target.value } : x)))
            }
          />
        </div>
      ))}
      <button
        type="button"
        className="mt-2 text-xs font-medium text-slate-500 hover:text-slate-800"
        onClick={() => setItems((all) => [...all, { label: '', kind: 'surplus_asset', value: '' }])}
      >
        Add a group item
      </button>

      <p className="mt-5 text-sm font-medium text-slate-800">The group itself</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Whether these businesses are worth more or less together than apart. Negative discounts,
        positive adds; leave it blank and the group is taken to be worth exactly its corrected
        parts. It is the first thing a reader will put back to you, so it needs a reason.
      </p>
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-slate-600">
          Rate, as a decimal
          <input
            className={INPUT}
            inputMode="decimal"
            value={rate}
            placeholder="-0.10"
            onChange={(e) => setRate(e.target.value)}
          />
        </label>
        <label className="text-sm text-slate-600 sm:col-span-2">
          Why
          <input
            className={INPUT}
            value={adjustmentBasis}
            placeholder="Three unrelated businesses under one board; no buyer wants all three."
            onChange={(e) => setAdjustmentBasis(e.target.value)}
          />
        </label>
      </div>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Bridging…' : 'Bridge it'}
        </button>
      </div>
    </div>
  );
}
